import { Component, type ReactNode } from "react";

/**
 * Keeps one screen's crash inside that screen.
 *
 * The village renders BEHIND the tutorial. On 24 Sep a Pixi error in the
 * village canvas (a texture-pool crash from `app.destroy(true)`) had no
 * boundary to stop at, so React unmounted the whole app — and the player saw
 * the tutorial "crash" on Next after the road step, although nothing in the
 * tutorial was wrong. With this around <main>, a broken background screen
 * shows a small message instead, and the tutorial on top keeps working.
 */
export class ScreenBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  override state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error): { error: Error } {
    return { error };
  }

  override componentDidCatch(error: Error): void {
    console.error("[ScreenBoundary]", error);
  }

  override render(): ReactNode {
    if (!this.state.error) return this.props.children;
    return (
      <div className="grid h-full place-items-center px-6 text-center" data-testid="screen-error">
        <div>
          <p className="text-sm text-ink">This screen hit a problem.</p>
          <button
            type="button"
            onClick={() => this.setState({ error: null })}
            className="mt-3 h-11 rounded-lg bg-gold px-5 text-xs font-bold text-bg"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }
}
